import { useState, useImperativeHandle, forwardRef, useMemo } from "react";
import ReusableDialog from "./ReusableDialog";
import DeleteAction from "./actions/DeactivateToggle";
import RechargeAction from "./actions/Recharge";
import EditAction from "./actions/Edit";
import Adjust from "./actions/Adjust";
import Points from "./actions/Points";
import RefundAccept from "./actions/RefundAccept";
import TelecomSuperMasterForm from "./actions/TelecomSuperMasterForm";
import ZoomAccountForm from "./actions/ZoomAccountForm";
import CuttingMeetingForm from "./actions/CuttingMeetingForm";
import ZoomEdit from "./actions/ZoomEdit";
import Refund from "./actions/Refund";
import CreateOwnerForm from "./actions/CreateOwnerForm";
import CreateMasterForm from "./actions/CreateMasterForm";
import CreateSubscriberForm from "./actions/CreateSubscriberForm";
import CreateManager from "./actions/CreateManager";
import DeleteManager from "./actions/DeleteManager";
import DeleteMaster from "./actions/DeleteMaster";
import DeleteHost from "./actions/DeleteHost";
import DeactivateToggle from "./actions/DeactivateToggle";
import DeleteZoomAccount from "./actions/DeleteZoomAccount";
import DeleteMeeting from "./actions/DeleteMeeting";
import ReversePoints from "./actions/ReversePoints";
import MeetingEdit from "./actions/MeetingEdit";
import ResetPassword from "./actions/ResetPassword";
import RechargeSuperMaster from "./actions/RechargeSuperMaster";
import ReverseSuperMasterPoints from "./actions/ReverseSuperMasterPoints";
import OwnerRecharge from "./actions/OwnerRecharge";
import OneMonthRecharge from "./actions/OneMonthRecharge";
import ThreeMonthsRecharge from "./actions/ThreeMonthsRecharge";
import CreateHighspeedHost from "./actions/CreateHostsForm";
import UnbindDevice from "./actions/UnbindDevice";
import DeleteHosts from "./actions/DeleteHosts";
import UnlinkMeeting from "./actions/UnlinkMeeting";
import LinkMeeting from "./actions/LinkMeeting";
import BlockedToggle from "./actions/BlockedToggle";
import SubscriberRechargeByOwner from "./actions/SubscriberRechargeByOwner";
import SettleMasterUsedPoints from "./actions/SettleMasterUsedPoints";
import SettleSuperMasterUsedPoints from "./actions/SettleSuperMasterUsedPoints";

const ReusableActions = forwardRef(({ refreshTableData }, ref) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [data, setCurrentData] = useState(null);

  const onCancel = () => {
    setIsDialogOpen(false);
    setTimeout(() => {
      setCurrentAction(null);
      setCurrentData(null);
    }, 250);
  };

  const onSuccess = () => {
    refreshTableData();
  };

  useImperativeHandle(ref, () => ({
    updateChildState() {
      setIsDialogOpen((prev) => !prev);
    },
    updateCurrentAction(action) {
      setCurrentAction(action);
    },
    updateCurrentData(data) {
      setCurrentData(data);
    },
  }));

  const dialogTitle = useMemo(() => {
    if (!currentAction) return null;

    const name = data?.name || "";
    const username = data?.username || "";
    const id = data?.id || "";

    const titles = {
      deactivate: `${
        data?.enabled ? "Deactivate" : "Activate"
      } Account (${username})`,
      recharge: `Recharge Subscriber (${username})`,
      edit: `Edit Subscriber (${username})`,
      adjust: `Adjust Subscriber (${username})`,
      acceptRefund: `Refund (${username})`,
      createSubscriber: `Create New Subscriber`,
      createOwner: `Create New Owner`,
      create_slot: `Create New Slot`,
      createMaster: `Create New Master`,
      createSuperMaster: `Create New Super Master`,
      create_telecom_host: `Create Subscriber`,
      create_telecom_master: `Create Master`,
      create_telecom_supermaster: `Create Super Master`,
      createZoomAccount: `Create Zoom Account`,
      createManager: `Create Manager`,
      create_cuttings_meeting: `Create New Meeting`,
      create_streaming_meeting: `Create New Streaming Meeting`,
      create_manager: `Create New Manager`,
      zoom_account_edit: `Edit Zoom Account (${username})`,
      delete_manager: `Delete Manager (${username})`,
      delete_zoom_account: `Delete Zoom Account (${username})`,
      delete_meeting: `Delete Meeting (${name})`,
      delete_host: `Delete Subscriber (${username})`,
      delete_master: `Delete Master (${username})`,
      reverse_points: `Reverse Points (${username})`,
      edit_meeting: `Edit Meeting (${name})`,
      owner_sub_recharge: `Recharge Subscriber (${username})`,
      rechargePoints: `Recharge Points (${username})`,
      subscriberRefund: `Refund Subscription (${username})`,
      resetPassword: `Reset Password`,
      rechargeSuperMasterPoints: `Recharge Super Master (${username})`,
      settle_sm_used_points: `Settle Points (${username})`,
      settle_used_points: `Settle Points (${username})`,
      reverse_super_master_points: `Reverse Super Master (${username})`,
      owner_recharge: `Recharge Owner (${username})`,
      one_month_recharge: `1 Month Recharge (${username})`,
      three_months_recharge: `3 Months Recharge (${username})`,
      device_change: `Device Unbind (${username})`,
      delete_hosts: `Delete Host (${username})`,
      create_hosts: `Create Host`,
      unlink_meeting: `Unlink Meeting (${username})`,
      link_meeting: `Link Meeting (${username})`,
      user_blocked: `${
        data?.blocked ? "Unblock" : "Block"
      } Account (${username})`,
    };

    return titles[currentAction] || "Action";
  }, [currentAction, data]);

  const dialogContent = useMemo(() => {
    const props = { onCancel, data, onSuccess };

    const components = {
      deactivate: <DeleteAction {...props} />,
      recharge: <RechargeAction {...props} />,
      edit: (
        <EditAction onCancel={onCancel} userData={data} onSuccess={onSuccess} />
      ),
      zoom_account_edit: <ZoomEdit {...props} />,
      adjust: <Adjust {...props} />,
      rechargePoints: <Points {...props} />,
      subscriberRefund: <Refund {...props} />,
      acceptRefund: <RefundAccept {...props} />,
      create_telecom_master: (
        <CreateMasterForm onCancel={onCancel} onSuccess={onSuccess} />
      ),
      createOwner: (
        <CreateOwnerForm onCancel={onCancel} onSuccess={onSuccess} />
      ),
      create_telecom_supermaster: (
        <TelecomSuperMasterForm onCancel={onCancel} onSuccess={onSuccess} />
      ),
      create_telecom_host: (
        <CreateSubscriberForm onCancel={onCancel} onSuccess={onSuccess} />
      ),
      createZoomAccount: (
        <ZoomAccountForm onCancel={onCancel} onSuccess={onSuccess} />
      ),
      create_cuttings_meeting: (
        <CuttingMeetingForm onCancel={onCancel} onSuccess={onSuccess} />
      ),
      create_manager: (
        <CreateManager onCancel={onCancel} onSuccess={onSuccess} />
      ),
      deactivate_toggle: <DeactivateToggle {...props} />,
      delete_manager: <DeleteManager {...props} />,
      delete_zoom_account: <DeleteZoomAccount {...props} />,
      delete_meeting: <DeleteMeeting {...props} />,
      delete_host: <DeleteHost {...props} />,
      delete_master: <DeleteMaster {...props} />,
      reverse_points: <ReversePoints {...props} />,
      edit_meeting: <MeetingEdit {...props} />,
      resetPassword: <ResetPassword {...props} />,
      rechargeSuperMasterPoints: <RechargeSuperMaster {...props} />,
      reverse_super_master_points: <ReverseSuperMasterPoints {...props} />,
      owner_recharge: <OwnerRecharge {...props} />,
      one_month_recharge: <OneMonthRecharge {...props} />,
      three_months_recharge: <ThreeMonthsRecharge {...props} />,
      device_change: <UnbindDevice {...props} />,
      create_hosts: <CreateHighspeedHost {...props} />,
      delete_hosts: <DeleteHosts {...props} />,
      unlink_meeting: <UnlinkMeeting {...props} />,
      link_meeting: <LinkMeeting {...props} />,
      user_blocked: <BlockedToggle {...props} />,
      owner_sub_recharge: <SubscriberRechargeByOwner {...props} />,
      settle_sm_used_points: <SettleSuperMasterUsedPoints {...props} />,
      settle_used_points: <SettleMasterUsedPoints {...props} />,
    };

    return components[currentAction] || null;
  }, [currentAction, data]);

  return (
    <div>
      <ReusableDialog
        isOpen={isDialogOpen}
        onCancel={onCancel}
        title={dialogTitle}
      >
        {dialogContent}
      </ReusableDialog>
    </div>
  );
});

export default ReusableActions;
